/**
 * Der Gesprächskern des Overlay-Assistenten: Transkript, Eingabezeile und die
 * komplette Stream-Logik — extrahiert aus OverlayBot, damit derselbe Chat in
 * zwei Hüllen leben kann: im schwebenden Eck-Panel (OverlayBot) und im rechts
 * andockenden Seitenpanel des AppShell ("Fragen").
 *
 * Deliberately not a second full chat: no upload, no resources, no
 * sources — those belong to ChatExchange. Same request processing and the same
 * tool set as /chatbot without tool selection, no pinned tool (see
 * overlayBotStream).
 *
 * Clarification from the product owner: conceptually this is **the same**
 * chat as `/chatbot`, just with the context of the open page added — not a
 * second, weaker source of answers. The portal guide adds two things: links
 * in the answers navigate *within the portal* instead of reloading the bundle
 * (see `markdownLinkComponent`), and on a **route jump** that the panel itself
 * triggered, it stays put — it comes along, keeps the conversation and the
 * running stream, and says so in a grey line.
 *
 * Three limits of this promise, so that it does not promise more than it keeps:
 *
 * 1. **Route jumps only.** The reset effect depends on the path, not the
 *    query (`PORTAL_GUIDE.md` §6.6). A jump `?panel=a` → `?panel=b` within
 *    `/settings` does not even go through this effect: the conversation stays
 *    there anyway, but the grey line is omitted.
 * 2. **Not to `/chatbot`.** There `AppShell` does not render the panel
 *    (`!onChat && assistantOpen`); a guided jump to a `/chatbot` address
 *    tears it down, the running stream is aborted and the in-memory-only
 *    transcript is gone. As long as the renderer does not mark such targets
 *    as a switch, this is the known exception.
 * 3. **Only with a page title.** The grey line names the location or is
 *    omitted — see `resolvePageTitle`.
 *
 * Since the **handoff from the main chat** (`src/guide/chatHandoff.ts`) the
 * promise also holds in the opposite direction: whoever follows a guided link
 * on `/chatbot` necessarily leaves the chat (there is no panel beside the page
 * there). Instead of dropping them on the target page without a conversation,
 * the navigator registers a handoff, `AppShell` opens this panel, and the panel
 * takes over the conversation here including its history — the same
 * conversation id, the same messages, continuing right where it left off.
 *
 * Since 22.09.2026 the promise "the same chat" is also **visible**: the
 * transcript renders `ChatConversation`/`ChatMessage` from the main chat, and
 * the input is its `ChatPromptField`. Own speech bubbles, an own Markdown
 * renderer and an own parsing of the `<div class="progress">` steps no longer
 * exist here — they were a second, weaker rendering of the same answers. The
 * panel now only brings what the main chat does not know: the side note
 * (`panelNote`) and the narrow column.
 *
 * The column is about more than its width: the main chat attaches the spacing
 * between two messages, the list margins in Markdown and the hidden horizontal
 * overflow to its own wrappers (`.chatBot`, `.chat-conversation-area`), not to
 * the shared components. `OverlayBotChat.module.sass` therefore mirrors them
 * for this wrapper — with the same values, so that "the same chat" also means
 * the same spacing.
 *
 * What the panel deliberately does **not** show, although the renderer could:
 * thumbs rating and the secondary "copy as HTML" button (`compactActions`
 * — a thumb would not reach the server without a message index),
 * resource tiles, attachments and tool selection. Since stage 2 of the portal
 * guide the panel reads exactly **two** blocks from the answer payload —
 * `navigate` (a button with an address) and `highlight` (the pointer at an
 * element of the page); they need no display machinery. It still discards
 * everything else, so `resources` carries at most one `navigate` block.
 *
 * **Deliberately given up:** the old panel-specific renderer stripped all tags
 * from the answer text before display. The shared renderer does not — it runs
 * `rehypeRaw`, because the main chat needs raw HTML in the answers
 * (`<div class="progress">`, embedded resources). The panel thus carries the
 * same exposure as `/chatbot`: whatever HTML the model or a tool writes becomes
 * real DOM. That is the price of parity and is recorded here so that nobody
 * mistakes it for an oversight; only the anchor is secured
 * (`markdownLinkComponent` validates every address). Any mitigation, if it
 * comes, belongs in the **shared** renderer — not back in the panel, otherwise
 * two renderings stand side by side again.
 */

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useAtom } from "jotai";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUp, ArrowUpRight, Square } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import { authenticationAtom } from "../../State";
import { userAPI } from "../../UserAPI";
import { useOverlayBotContext } from "./OverlayBotContext";
import { streamOverlayAnswer } from "./overlayBotStream";
import { isSelfNavigation } from "../../guide/useGuideNavigate";
import { requestAutoNavigate, useAutoNavigateRunner } from "../../guide/autoNavigate";
import { useChatSendRunner } from "../../guide/chatSend";
import {
  guideLocale,
  isAutoNavigate,
  isHighlightBlock,
  isNavigateBlock,
  navigateHrefFrom,
  spotlightFromHighlightBlock,
  type HighlightBlock,
  type NavigateBlock,
} from "../../guide/guideBlocks";
import { requestSpotlight } from "../../guide/spotlight";
import {
  consumeChatHandoff,
  isChatHandoffFor,
  useChatHandoff,
} from "../../guide/chatHandoff";
import { markPanelReady, whenMorphIdle } from "../../guide/morph/assistantMorph";
import ScrollToBottomButton from "../ScrollToBottomButton";
import { ChatConversation } from "../ChatConversation";
import { ChatPromptField } from "../ChatPromptField";
import { Button as MotionButton } from "@/components/motion/button";
import {
  ASSISTANT_PANEL_ACTION_ROW_CLASS,
  ASSISTANT_PANEL_FORM_CLASS,
  ASSISTANT_PANEL_INPUT_BOX_CLASS,
  ASSISTANT_PANEL_TRANSCRIPT_CLASS,
} from "./assistantPanelChrome";
import { cn } from "@/lib/utils";
import styles from "./OverlayBotChat.module.sass";

/**
 * One transcript row in the shape `ChatMessage` expects.
 *
 * `type` carries the values of the `MessageType` enum from
 * `@the-real-insight/tri-model` (`userMessage` / `botMessage`); they appear here
 * as strings so that the panel does not pull the whole model package into its
 * bundle. `panelNote` is added: not a conversation turn but a side note of the
 * panel — currently only the „Ich bin mitgekommen" line after a self-triggered
 * navigation. It is never sent to the server.
 *
 * `id` belongs to the panel (the running stream uses it to write into its
 * bubble), `resources` carries at most the portal guide's `navigate` block:
 * the panel discards everything else from the payload. The block additionally
 * needs its placeholder in `content` — that is exactly how `ChatMessage` finds
 * an embedded resource.
 *
 * Whether a bubble is currently being written is **not** stored on the message:
 * the shared renderer derives it from `pendingRequest` and the position of the
 * last conversation turn (`ChatConversation`/`ChatMessage`). A separate field
 * here would be a second, slightly diverging truth — that is why there is no
 * `streaming` any more.
 */
interface Message {
  id: string;
  type: "userMessage" | "botMessage" | "panelNote";
  content: string;
  resources: NavigateBlock[];
  /** Only for `panelNote`: the `data-testid` under which the row is tested. */
  testId?: string;
}

/** The `data-testid` of the side note — the UI tests rely on it. */
const SYSTEM_LINE_TEST_ID = "overlay-bot-system-line";

/**
 * The corporate accent, taken from the customer theme rather than fixed here.
 * `--theme-secondary-light` is what `CustomerThemeProvider` writes per customer;
 * the chain falls back the same way the rest of the app does, so a customer
 * without a theme still gets the house colour instead of a stray blue.
 */
export const OVERLAY_BOT_ACCENT =
  "var(--theme-secondary-light, var(--secondary-color-light, #ae629a))";

let sequence = 0;
const nextId = () => `m${Date.now().toString(36)}-${(sequence += 1)}`;

/**
 * Strips an appended product/brand name: "The Real Insight -
 * Unleash the power …" → "The Real Insight".
 */
const TITLE_SUFFIX = /\s+[·–—|-]\s+.*$/;

/**
 * How long to wait for a still-empty header title. Pages that set their title
 * only after a data fetch (`EntityPage` sets `setTitle('')` while the entity
 * loads) need a network round trip; if the heading is still empty after that,
 * the line is omitted.
 */
const TITLE_WAIT_MS = 3000;

/**
 * The title of the page the user is currently on — for the
 * „Ich bin mitgekommen" line.
 *
 * There is no clean data source for it, and that is not an omission of this
 * file: the title lives as `useState` in `AppShell` and is passed to the child
 * route via the outlet context (`setTitle`). The questions panel is a
 * *sibling* of the `<Outlet>` and cannot reach that context;
 * `OverlayBotContext` only carries `context`/`helpText`; and `document.title`
 * holds only the brand name (`public/index.html` writes it once at startup,
 * no page touches it afterwards).
 *
 * So we read the heading that `SiteHeader` renders from exactly that state —
 * `header h1`, the shell's only `<header>`.
 *
 * What matters is the *existence* of the element, not its text:
 * `SiteHeader` always renders the `h1`, even with an empty title. A fallback to
 * `document.title` on an empty `h1` would therefore name not the location but
 * the brand name („du bist jetzt in The Real Insight"). Hence:
 *
 * - no `header h1` in the document (floating overlay outside the AppShell)
 *   → `document.title` without the product-name suffix;
 * - `h1` present and filled → that text;
 * - `h1` present and empty → wait for the title (`MutationObserver`, timeout
 *   `TITLE_WAIT_MS`); if it never comes, the line is dropped entirely rather
 *   than naming the wrong location.
 */
const resolvePageTitle = (onTitle: (title: string) => void): (() => void) => {
  const heading = document.querySelector("header h1");

  if (!heading) {
    const documentTitle = document.title.trim().replace(TITLE_SUFFIX, "").trim();
    if (documentTitle) onTitle(documentTitle);
    return () => undefined;
  }

  const read = () => heading.textContent?.trim() || "";

  const immediate = read();
  if (immediate) {
    onTitle(immediate);
    return () => undefined;
  }

  let done = false;
  const finish = (title: string) => {
    if (done) return;
    done = true;
    observer.disconnect();
    window.clearTimeout(deadline);
    if (title) onTitle(title);
  };

  const observer = new MutationObserver(() => {
    const title = read();
    if (title) finish(title);
  });
  observer.observe(heading, { childList: true, characterData: true, subtree: true });
  const deadline = window.setTimeout(() => finish(""), TITLE_WAIT_MS);

  return () => finish("");
};

/**
 * The placeholder with which `ChatExchange` weaves a resource **into** the
 * answer text. It is baked into the stored content of every answer with a
 * chart, table, map, list or link.
 */
const INLINE_RESOURCE_PLACEHOLDER =
  /<div[^>]*class=["']inline-resource["'][^>]*>\s*<\/div>/gi;

/**
 * Maps `Conversation.messages` from `@the-real-insight/tri-model` into this
 * panel's transcript. Question and answer are kept; everything else is dropped:
 * resources, sources, confirmations and timestamps belong in `ChatExchange`.
 *
 * The woven-in `<div class="progress">` HTML **stays in the text** — unlike
 * before, when the panel discarded it while rendering. The shared renderer
 * extracts it at display time as a collapsed step group
 * (`splitAgentStepSegments` in `ChatMessage`), just as in the main chat.
 *
 * The placeholders of **embedded resources**, on the other hand, must go. They
 * belong to a payload the panel discards (`resources: []`), and the shared
 * renderer resolves them via exactly that field: whatever it does not find
 * there, it draws as a red "Resource not found" box in the middle of the
 * answer. Previously this could not show up — the old panel renderer stripped
 * all tags.
 */
export function adoptedMessages(conversation: unknown): Message[] {
  const raw = (conversation as { messages?: unknown })?.messages;
  if (!Array.isArray(raw)) return [];

  const adopted: Message[] = [];
  for (const entry of raw) {
    const type = (entry as { type?: unknown })?.type;
    if (type !== "userMessage" && type !== "botMessage") continue;
    const content = (entry as { content?: unknown })?.content;
    const text =
      typeof content === "string" ? content.replace(INLINE_RESOURCE_PLACEHOLDER, "") : "";
    if (!text.trim()) continue;
    adopted.push({ id: nextId(), type, content: text, resources: [] });
  }
  return adopted;
}

interface Properties {
  className?: string;
  /** Fokussiert die Eingabe, sobald die Hülle geöffnet wird. */
  active?: boolean;
}

export default function OverlayBotChat({ className, active = true }: Properties) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const [authentication] = useAtom(authenticationAtom) as any;
  const page = useOverlayBotContext();
  const location = useLocation();
  const navigate = useNavigate();
  /** Handoff from the main chat, if the user came from there. */
  const handoff = useChatHandoff();
  /**
   * Portal guide, stage 2: the effect that executes a registered automatic jump
   * — the same one here as in the main chat (`src/guide/autoNavigate.ts`).
   * `onDone` is a stream callback and cannot hold `useGuideNavigate`.
   *
   * No handoff is created here: handoffs only exist on `/chatbot`, and there
   * `AppShell` does not render this panel. But `guideNavigate` sets the
   * self-navigation mark here too — otherwise the reset effect below would
   * throw away the panel's own conversation on arrival.
   */
  useAutoNavigateRunner();
  // Stage 3: click on follow-up question/card → message. `send` is only defined further down; the
  // ref holds the current version, and the runner gets a stable function.
  const sendRef = useRef<((question: string) => Promise<void>) | null>(null);
  const sendViaRef = useRef((text: string) => sendRef.current?.(text)).current;
  useChatSendRunner(sendViaRef);

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [streaming, setStreaming] = useState(false);
  /**
   * Counts every reset — the conversation identity for `ChatConversation`.
   * The list remembers per `_id` how many messages were already there at first
   * sight, and only fades in what arrives afterwards. Without a change here that
   * mark would stay at its old value after a reset, and the first question in
   * the new conversation would appear without animation. Deliberately state and
   * not `generationRef`: a ref does not change the output.
   */
  const [resetCount, setResetCount] = useState(0);
  /**
   * Counts every adopted handoff — the second part of the same identity.
   * An adoption prepends a whole history at the **front**; without a change
   * here the fade-in mark would stay at "one message" (the help text), and the
   * entire imported history would pop in at once. In the main chat an opened
   * history stands still — it should here too.
   */
  const [adoptionCount, setAdoptionCount] = useState(0);
  /**
   * Did this panel arrive via a handoff from the main chat that brought a
   * conversation along? Then the opening bubble („Ich helfe dir bei der Arbeit
   * mit dem Portal …") is omitted — the conversation is already under way, and
   * the bubble would read like a fresh start (product owner's requirement of
   * 22.09.2026). Deliberately set **synchronously** on adoption, not only when
   * the history arrives: otherwise the bubble would show for the duration of a
   * network round trip and then vanish again.
   *
   * The price: if fetching the history fails, the panel stays empty instead of
   * opening with the help text. The thread is still adopted and the next
   * question continues it — an opening bubble in the middle of an ongoing
   * conversation would be the worse answer.
   */
  const [adoptedFromChat, setAdoptedFromChat] = useState(false);
  /**
   * Address for which the grey „Ich bin mitgekommen" line is promised —
   * `null` while nothing is pending. Deliberately state and not a ref: it
   * **drives** the effect below that schedules the line; a ref could neither
   * start nor cancel it.
   */
  const [followUpAddress, setFollowUpAddress] = useState<string | null>(null);

  const abortRef = useRef<null | (() => void)>(null);
  const conversationRef = useRef<string | null>(null);
  /** The page context goes with the first question only — see OverlayBotContext. */
  const contextSentRef = useRef(false);
  /** The transcript wrapper — the jump button looks for the scroller inside it. */
  const transcriptRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** Incremented on every page reset to invalidate in-flight operations. */
  const generationRef = useRef(0);
  /**
   * The query of the current address — deliberately a ref: it must not trigger
   * the reset effect (a change `?panel=a` → `?panel=b` is not a change of
   * subject), but it is needed for matching against the jump target.
   */
  const searchRef = useRef(location.search);
  /** Address for which the "came along" line is already shown (once per jump). */
  const acknowledgedRef = useRef<string | null>(null);
  /**
   * Id of the handoff already adopted. Deliberately the **id** and not a
   * boolean one-shot flag: under `<React.StrictMode>` the effect runs twice in
   * development mode — seeing the same id twice must have no effect, but a new
   * id afterwards must take effect again.
   */
  const adoptedHandoffRef = useRef<string | null>(null);
  /**
   * Path on which a handoff was adopted. The reset effect runs a second time on
   * the *same* page as soon as it reports its page context late — and its
   * acknowledgement depends on `pathname + search`, which a target page can
   * rewrite behind its back with `setSearchParams(…, { replace: true })`.
   * Without this marker that second run would fall into the reset branch and
   * throw away the just-adopted conversation — for good, since the handoff has
   * been consumed.
   */
  const adoptedPathnameRef = useRef<string | null>(null);
  /**
   * Has a question been asked since the adoption? Then the history fetch may
   * already contain the new question (the server has saved it in the meantime),
   * and it would appear twice in the panel — once optimistically, once from the
   * history. Only in this case is the insertion de-duplicated; without a
   * question of our own the history stays untouched, even if it says the same
   * thing twice.
   */
  const askedSinceAdoptionRef = useRef(false);
  /**
   * Only read `t`, never use it as a dependency: a language switch must not
   * throw away the conversation.
   */
  const tRef = useRef(t);
  tRef.current = t;

  /**
   * Update the query only in the commit, not during render: otherwise a
   * discarded concurrent render would leave an address in the ref that was
   * never committed — and that is exactly what the reset effect below uses to
   * match against the jump target. A layout effect runs in the same commit
   * before the passive reset effect, so the dependency list stays as it is.
   */
  useLayoutEffect(() => {
    searchRef.current = location.search;
  }, [location.search]);

  /*
   * Following the stream while the reader is at the bottom is now handled by
   * the `MessageScroller` in `ChatConversation` — the same mechanism as in the
   * main chat. The panel's own scroll effects are therefore gone; the
   * jump-to-bottom button remains and grabs the scroller (`.messages`) from
   * the transcript, just like `ChatExchange` does.
   */

  // `preventScroll`: focus arrives in exactly the commit in which the morph's
  // crossfade starts — without this request the browser would scroll the
  // `sticky` column into view in the most visible frame and shift the target
  // page underneath.
  useEffect(() => {
    if (active) inputRef.current?.focus({ preventScroll: true });
  }, [active]);

  useEffect(
    () => () => {
      abortRef.current?.();
    },
    [],
  );

  // A new page is a new subject: drop the thread rather than answer the next
  // question against the previous screen's context.
  //
  // Exception: the jump came from an answer of this panel itself — the
  // navigator marks it beforehand (`markSelfNavigation` in `useGuideNavigate`).
  // Then a reset would be the opposite of guidance: the assistant would forget
  // itself on arrival. `generationRef` therefore stays unchanged in this
  // branch — a running stream survives the navigation and keeps writing while
  // the user is already on the target page.
  //
  // The early exit is decided by the **acknowledgement**, not the clock: the
  // effect also depends on `page.helpText` and `page.context`, and a page that
  // registers its context only after a network fetch makes it run a second
  // time for the same jump seconds later — after the 2000 ms window of
  // `isSelfNavigation` has expired. If the time check came first, that second
  // run would fall into the reset branch and throw away the conversation and
  // the running stream after all. `acknowledgedRef` thus carries both: the
  // one-time promise for the grey line (also under `<React.StrictMode>`) and
  // the protection of the acknowledged address while the panel stays there.
  useEffect(() => {
    const address = `${location.pathname}${searchRef.current}`;

    if (acknowledgedRef.current === address) return;

    if (isSelfNavigation(address)) {
      acknowledgedRef.current = address;
      // The page context of the *target page* has not been sent yet: the
      // conversation stays, but so far only carries the context of the page
      // that was left. The next question therefore attaches it again —
      // transcript, conversation id and generation remain untouched.
      contextSentRef.current = false;

      // The grey line is not scheduled by this effect but by its own effect
      // below — it is only commissioned here (see there for why).
      setFollowUpAddress(address);
      return;
    }

    // A conversation adopted here stays as long as the panel is on *its* page:
    // the second run for the same page (late-reported context, rewritten
    // query, `<React.StrictMode>`) is not a change of subject. A real path
    // change still resets as usual.
    if (adoptedPathnameRef.current === location.pathname) {
      acknowledgedRef.current = address;
      return;
    }
    adoptedPathnameRef.current = null;
    askedSinceAdoptionRef.current = false;

    setFollowUpAddress(null);
    acknowledgedRef.current = null;
    abortRef.current?.();
    abortRef.current = null;
    setStreaming(false);
    conversationRef.current = null;
    contextSentRef.current = false;
    setMessages([]);
    setAdoptedFromChat(false);
    generationRef.current += 1;
    setResetCount((previous) => previous + 1);
  }, [page.helpText, page.context, location.pathname]);

  /**
   * The grey „Ich bin mitgekommen" line — in its **own** effect, not in the
   * branch of the reset effect above.
   *
   * The reason is the double effect execution under `<React.StrictMode>`: in
   * development mode React mounts every effect, immediately tears it down and
   * mounts it again. When the promise lived in the reset effect, it was torn
   * down along with it (`followUpRef` hung in the cleanup function of the abort
   * effect further up) — and the reset effect's second pass bailed out on its
   * own acknowledgement (`acknowledgedRef.current === address`) without
   * rescheduling it. So in development mode the line was **always** missing,
   * even though the adoption itself had succeeded.
   *
   * Here it depends on exactly one value — the address it was promised for.
   * That keeps both concerns together: the effect does *not* re-run when the
   * target page reports its page context late (which is why the promise could
   * never go into the reset effect's cleanup), and its own cleanup cleanly
   * rebuilds it on the second StrictMode pass. A reset cancels it
   * (`setFollowUpAddress(null)`), a further jump replaces it with the new
   * address.
   */
  useEffect(() => {
    if (!followUpAddress) return undefined;

    const generation = generationRef.current;
    let cancelled = false;
    let cancelTitle: null | (() => void) = null;

    // Read only after the commit: the target page sets the header title in its
    // own effect (`setTitle` from the outlet context). That effect runs before
    // this one, but only takes effect in the next render — read synchronously,
    // the line would still show the title of the *old* page.
    const timer = window.setTimeout(() => {
      cancelTitle = resolvePageTitle((title) => {
        // The line belongs in *this* conversation: a reset in between
        // (generation) or a cancelled promise (`cancelled`) makes it moot. And
        // it does not belong in an empty transcript at all — then this panel
        // never asked anything, and the jump came from another renderer (e.g.
        // the agent services' test chat).
        //
        // An adopted conversation counts as the panel's own conversation, even
        // while its messages are still on their way: on a handoff from the main
        // chat `conversationRef` is set immediately, but fetching the history
        // needs a network round trip — without this condition the line would
        // be missing exactly when it is needed most.
        if (cancelled || generationRef.current !== generation) return;
        // Only once no morph is running any more: under the flying shell
        // (`src/guide/morph/`) the line would appear unnoticed, and its
        // `.oa-enter` animation would be wasted. Without a running morph
        // `whenMorphIdle()` resolves immediately — so nothing changes on mobile
        // or with `prefers-reduced-motion`. Both checks then run **again**: a
        // reset or a further jump may lie between promise and resolution.
        void whenMorphIdle().then(() => {
          if (cancelled || generationRef.current !== generation) return;
          setMessages((previous) =>
            previous.length === 0 && !conversationRef.current
              ? previous
              : [
                  ...previous,
                  {
                    id: nextId(),
                    type: "panelNote",
                    content: tRef.current("overlayBot.followed", { title }),
                    resources: [],
                    testId: SYSTEM_LINE_TEST_ID,
                  },
                ],
          );
        });
      });
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      cancelTitle?.();
      cancelTitle = null;
    };
  }, [followUpAddress]);

  /**
   * Adoption of a handoff from the main chat. Deliberately placed **after** the
   * reset effect: both run in the same commit on mount, and only in this order
   * does the reset not overwrite the just-adopted conversation again.
   *
   * The conversation id is set **synchronously**, the history is fetched only
   * afterwards. If the fetch fails, the transcript stays empty — the
   * conversation is still adopted, so the next question continues in the same
   * thread instead of opening a second one. There is no error log for this:
   * the user sees an empty panel and keeps asking; a console line per jump
   * would just be noise.
   *
   * Whether a stale response is discarded is decided solely by `generationRef`
   * — **no** cleanup function with an abort flag. Picking up the handoff
   * consumes it, which changes module state, and so the effect immediately runs
   * a second time: a cleanup function would abort its own fetch before the
   * server has even answered.
   */
  useEffect(() => {
    // Bound to the address, not just the clock: a handoff that nobody picked
    // up (target outside the shell) must not adopt the main chat's thread on
    // the next page the user navigates to *themselves* — the same boundary
    // that `markSelfNavigation` draws via its target.
    if (!isChatHandoffFor(handoff, `${location.pathname}${location.search}`)) return;
    if (!handoff || adoptedHandoffRef.current === handoff.id) return;
    adoptedHandoffRef.current = handoff.id;
    adoptedPathnameRef.current = location.pathname;
    askedSinceAdoptionRef.current = false;
    consumeChatHandoff();

    // The target page's context has not been sent yet — the next question
    // carries it along (the self-navigation branch above does the same).
    contextSentRef.current = false;

    const conversationId = handoff.conversationId;
    const handoffId = handoff.id;
    if (!conversationId) {
      // Nothing to fetch: the panel is immediately as ready as it will get.
      markPanelReady(handoffId);
      return;
    }
    conversationRef.current = conversationId;
    setAdoptedFromChat(true);

    const generation = generationRef.current;

    void (async () => {
      let adopted: Message[] = [];
      try {
        adopted = adoptedMessages(
          await userAPI.getConversation(authentication.customer, conversationId),
        );
      } catch {
        // Failure is a result too: the morph must not wait for it longer than
        // necessary — otherwise it would run into its cap.
        markPanelReady(handoffId);
        return;
      }
      // The signal the morph's handover waits for: from here on the real panel
      // shows the same history as the preview in the flying shell.
      markPanelReady(handoffId);
      if (generationRef.current !== generation) return;
      if (adopted.length === 0) return;
      // New identity for the list — in the same commit as the messages, so
      // that `ChatConversation` moves its fade-in mark to the full count and
      // the adopted history stands still.
      setAdoptionCount((previous) => previous + 1);
      // Prepend, don't append: by the time the response arrives, the grey
      // „Ich bin mitgekommen" line may already be there — the history belongs
      // before it.
      setMessages((previous) => {
        if (!askedSinceAdoptionRef.current) return [...adopted, ...previous];
        // A question was already asked while the history was on its way: the
        // server may already have included the new question. Anything that
        // repeats the role and text of a message already present is therefore
        // dropped — better one line too few from the history than the same
        // question twice in the panel.
        const seen = new Set(previous.map((message) => `${message.type}\u0000${message.content}`));
        return [
          ...adopted.filter((message) => !seen.has(`${message.type}\u0000${message.content}`)),
          ...previous,
        ];
      });
    })();
  }, [handoff, authentication?.customer, location.pathname, location.search]);

  const send = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || streaming) return;

    // From here on, a still-running history fetch may already get this
    // question back from the server — see `askedSinceAdoptionRef`.
    askedSinceAdoptionRef.current = true;
    setInput("");
    const botId = nextId();
    setMessages((previous) => [
      ...previous,
      { id: nextId(), type: "userMessage", content: trimmed, resources: [] },
      { id: botId, type: "botMessage", content: "", resources: [] },
    ]);
    setStreaming(true);

    const generation = generationRef.current;

    const fail = () => {
      if (generationRef.current !== generation) return;
      setMessages((previous) =>
        previous.map((message) =>
          message.id === botId ? { ...message, content: t("overlayBot.error") } : message,
        ),
      );
      setStreaming(false);
      abortRef.current = null;
      contextSentRef.current = false;
    };

    try {
      if (!conversationRef.current) {
        const conversation = await userAPI.createConversation(authentication.customer);
        if (generationRef.current !== generation) return;
        conversationRef.current = String((conversation as any)?._id ?? "");
      }
    } catch {
      if (generationRef.current !== generation) return;
      fail();
      return;
    }

    if (generationRef.current !== generation) return;

    // The page context rides on the first question. Afterwards the conversation
    // carries its own history and repeating it would only crowd the window.
    const withContext =
      !contextSentRef.current && page.context
        ? `${trimmed}\n\n${page.context}`
        : trimmed;
    contextSentRef.current = true;

    /**
     * The blocks of this one answer. Deliberately local to the send and not in
     * a ref: a stream belongs to exactly one `send`, and an older answer must
     * not set the pointer of a newer one.
     */
    let guideNavigateBlock: NavigateBlock | null = null;
    let guideHighlightBlock: HighlightBlock | null = null;

    abortRef.current = streamOverlayAnswer(
      {
        conversationId: conversationRef.current || "",
        question: withContext,
        user: {
          firstName: authentication?.firstName,
          lastName: authentication?.lastName,
          email: authentication?.email,
        },
        locale: i18next.resolvedLanguage || "de",
      },
      {
        onToken: (chunk) => {
          if (generationRef.current !== generation) return;
          setMessages((previous) =>
            previous.map((message) =>
              message.id === botId
                ? { ...message, content: message.content + chunk }
                : message,
            ),
          );
        },
        /**
         * Portal guide, stage 2: `navigate` is attached to the bubble
         * **immediately** — in stream order, as the main chat does, and with the
         * placeholder in the text through which `ChatMessage` resolves an
         * embedded resource. `highlight` and the automatic jump wait for
         * `onDone`: before that the answer is not complete, and a jump in the
         * middle of the stream would take away the sentence that explains the
         * way to the user.
         */
        onGuideBlocks: (blocks) => {
          if (generationRef.current !== generation) return;
          for (const block of blocks) {
            if (isHighlightBlock(block)) {
              guideHighlightBlock = block;
              continue;
            }
            // At most one per answer (CONTRACT.md §7.3) — and none without a
            // usable address, otherwise a placeholder without a button would be
            // left in the text.
            if (guideNavigateBlock || !isNavigateBlock(block)) continue;
            if (!navigateHrefFrom(block)) continue;
            guideNavigateBlock = block;
            // The same identity in `reference` and `_stableId`: the inline
            // renderer looks up via `_stableId || reference || _id`, the tile
            // group at the end via `reference || _id` — only if both see the
            // same thing does the button appear once in the text and not again
            // below it.
            const reference = `${botId}-navigate`;
            const attached: NavigateBlock = { ...block, reference, _stableId: reference };
            setMessages((previous) =>
              previous.map((message) =>
                message.id === botId
                  ? {
                      ...message,
                      resources: [attached],
                      content: `${message.content}\n\n<div class="inline-resource" data-resource-id="${reference}"></div>\n\n`,
                    }
                  : message,
              ),
            );
          }
        },
        onDone: () => {
          if (generationRef.current !== generation) return;
          // The bubble carries no state of its own: that it is finished follows
          // from `streaming` — and that switches off the line below it.
          setStreaming(false);
          abortRef.current = null;

          // Now the answer is complete: show, and if the user asked for it
          // („öffne …"), jump. The pointer travels with the jump request so
          // that its search timeout (1.5 s) only starts on the target page;
          // without a jump it is registered immediately.
          const locale = guideLocale(i18next.resolvedLanguage);
          const spotlight = spotlightFromHighlightBlock(guideHighlightBlock, locale);
          const href = isAutoNavigate(guideNavigateBlock)
            ? navigateHrefFrom(guideNavigateBlock)
            : null;
          if (href) {
            const claimed = requestAutoNavigate({
              href,
              key: `${conversationRef.current ?? "overlay"}:${botId}:${href}`,
              spotlight: spotlight ?? undefined,
            });
            if (claimed) return;
          }
          if (spotlight) requestSpotlight(spotlight);
        },
        onError: fail,
      },
    );
  };
  sendRef.current = send;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void send(input);
  };

  /**
   * Stop the run — the same button, the same gesture as in the main chat.
   *
   * The panel has long held the handle for it (`abortRef`), but did not offer
   * it until now: an agent run could not be stopped here at all. Whatever has
   * already been written stays — just as with `onDone`; only nothing more is
   * added.
   */
  const stop = () => {
    abortRef.current?.();
    abortRef.current = null;
    setStreaming(false);
  };

  /**
   * Enter sends, Shift+Enter inserts a line break — the same convention as in
   * the main chat. `isComposing` protects the intermediate stage of IME input
   * from being sent by accident.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    void send(input);
  };

  /**
   * The transcript in the shape `ChatConversation` expects.
   *
   * The page's help text is its first answer, not a special case beside it: it
   * *is* the assistant's first utterance and should read that way. It travels
   * with the page context, so it does not belong in `messages` — otherwise the
   * reset would throw it away too and the target page would not have it.
   *
   * **Not** after a handoff from the main chat: there the conversation is
   * already under way, and an opening bubble in front of it would read like a
   * fresh start — see `adoptedFromChat`.
   */
  const conversation = useMemo(
    () => ({
      _id: `overlay-${resetCount}-${adoptionCount}`,
      messages: adoptedFromChat
        ? messages
        : [
            {
              id: "overlay-help",
              type: "botMessage" as const,
              content: page.helpText || t("overlayBot.defaultHelp"),
              resources: [] as never[],
            },
            ...messages,
          ],
    }),
    [adoptedFromChat, adoptionCount, messages, page.helpText, resetCount, t],
  );

  return (
    // The flex chain of the height: every link needs `min-h-0`, otherwise the
    // automatic minimum height (= content) overrides the wrapper's cap and the
    // input row slips below the window edge. A guard in
    // `OverlayBotChat.test.tsx` pins the chain.
    <div data-testid="overlay-bot-chat" className={cn("flex min-h-0 flex-1 flex-col", className)}>
      {/* The way back (28.09.2026): after a morph the reader is on a portal page with the panel;
          this row returns to the full chat, on the same conversation when the panel adopted one. */}
      <div className="flex shrink-0 justify-end px-2 pt-1">
        <button
          type="button"
          data-testid="overlay-bot-back-to-chat"
          onClick={() => {
            const id = conversationRef.current;
            navigate(id && !id.startsWith("overlay-") ? `/chatbot?conversationId=${encodeURIComponent(id)}` : "/chatbot");
          }}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[var(--oa-fg-4)] transition-colors hover:bg-[var(--oa-surface)] hover:text-[var(--oa-fg)]"
        >
          {t("overlayBot.backToChat")}
          <ArrowUpRight className="size-3.5" aria-hidden />
        </button>
      </div>
      {/* `relative` so the jump button can sit inside the transcript;
          `overflow-hidden` so a long transcript does not stretch the chain —
          scrolling happens in the `MessageScroller` below. */}
      <div
        ref={transcriptRef}
        data-testid="overlay-bot-transcript"
        className={cn(ASSISTANT_PANEL_TRANSCRIPT_CLASS, styles.transcript)}
      >
        <ScrollToBottomButton
          // The same handle as in `ChatExchange`: the scroller is the viewport
          // of the `MessageScroller`, which carries the old class `.messages`.
          scroller={() =>
            transcriptRef.current?.querySelector<HTMLElement>(".messages") ?? null
          }
          watch={conversation.messages.length}
        />
        {/*
          The same history as on `/chatbot`: the same bubbles, the same
          Markdown renderer including portal-internal links, the same collapsed
          step group, the same „Denke nach …" shimmer.

          `submitInput` serves the follow-up question buttons an answer may
          offer; `onFeedback` stays empty because the thumbs are not shown here
          at all (`compactActions`). Nobody selects resources — the panel has
          none.
        */}
        <ChatConversation
          conversation={conversation}
          pendingRequest={streaming}
          submitInput={(value: string) => {
            void send(value);
          }}
          onFeedback={() => undefined}
          compactActions
        />
      </div>

      {/* The input box cut like the main chat's: soft radius, a hairline border
          from the opacity ladder that darkens while typing. */}
      <form onSubmit={onSubmit} className={ASSISTANT_PANEL_FORM_CLASS}>
        <div className={ASSISTANT_PANEL_INPUT_BOX_CLASS}>
          <ChatPromptField
            ref={inputRef}
            data-testid="overlay-bot-input"
            className="px-1 pt-0.5 text-[15px] leading-6 text-[var(--oa-fg)] placeholder:text-[var(--oa-fg-4)]"
            minRows={2}
            maxRows={6}
            placeholder={t("overlayBot.placeholder")}
            value={input}
            onValueChange={setInput}
            onKeyDown={onKeyDown}
          />
          {/* One button, two jobs — as on /chatbot: the arrow gives way to the
              square as soon as the run starts, and the surface switches to the
              warning tone. The `data-testid`s stay panel-specific
              (`overlay-bot-…`) so that they do not mix with the main chat's in
              a shared DOM. */}
          <div className={ASSISTANT_PANEL_ACTION_ROW_CLASS}>
            <MotionButton
              type={streaming ? "button" : "submit"}
              size="icon"
              data-testid={streaming ? "overlay-bot-stop" : "overlay-bot-send"}
              aria-label={streaming ? t("overlayBot.stop") : t("overlayBot.send")}
              disabled={streaming ? false : !input.trim()}
              onClick={streaming ? stop : undefined}
              className={cn(
                "ml-auto h-9 w-9 shrink-0 rounded-full",
                streaming && "bg-destructive text-destructive-foreground hover:bg-destructive/90",
              )}
            >
              <AnimatePresence initial={false} mode="popLayout">
                <motion.span
                  key={streaming ? "stop" : "send"}
                  initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 3, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -3, scale: 0.8 }}
                  className="grid place-items-center"
                >
                  {streaming ? (
                    <Square className="size-3 fill-current" aria-hidden="true" />
                  ) : (
                    <ArrowUp className="size-4" aria-hidden="true" />
                  )}
                </motion.span>
              </AnimatePresence>
            </MotionButton>
          </div>
        </div>
      </form>
    </div>
  );
}
